import InputFieldOutlined from 'component/Inputs/dashboard-input'
import Select from 'component/Select/select'
import Loader from 'component/loader'
import SearchIcon from 'icon/search'
import SortIcon from 'icon/sort'
import React, { useEffect, useState } from 'react'

function Table({
  columns = [],
  rows = [],
  loader,
  isSearchable,
  isSortable,
  isDeepSearch,
  fixedHeader,
  height,
}) {
  const [filteredRows, setFilteredRows] = useState([])
  const [sortOrder, setSortOrder] = useState({})

  // Deep search helper (recursive for React elements)
  const deepSearch = (ReactObject, query) => {
    if (!ReactObject) return false

    if (typeof ReactObject === 'string') {
      return ReactObject.toLowerCase().includes(query.toLowerCase())
    }

    if (ReactObject.props?.children) {
      if (typeof ReactObject.props.children === 'string') {
        return ReactObject.props.children.toLowerCase().includes(query.toLowerCase())
      }
      if (Array.isArray(ReactObject.props.children)) {
        return ReactObject.props.children.some(child => deepSearch(child, query))
      }
      return deepSearch(ReactObject.props.children, query)
    }

    return false
  }

  const handleSearch = (e) => {
    const query = e.target.value?.trim().toLowerCase()
    if (!query) {
      return setFilteredRows(rows)
    }

    const filtered = rows.filter((row) => {
      return Object.values(row).some((value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(query)
        }
        if (isDeepSearch && React.isValidElement(value)) {
          return deepSearch(value, query)
        }
        return false
      })
    })

    setFilteredRows(filtered)
  }

  const handleSort = (accessor, currentOrder) => {
    const order = currentOrder === 'asc' ? 'desc' : 'asc'
    setSortOrder({ ...sortOrder, [accessor]: order })

    const sorted = [...(filteredRows.length ? filteredRows : rows)].sort((a, b) => {
      const valA = a[accessor] ?? ''
      const valB = b[accessor] ?? ''
      if (order === 'asc') return valA > valB ? 1 : -1
      return valA > valB ? -1 : 1
    })

    setFilteredRows(sorted)
  }

  useEffect(() => {
    setFilteredRows(rows)
  }, [rows])

  return (
    <div
      className="relative overflow-x-auto shadow-md sm:rounded-xl border border-gray-200"
      style={{ maxHeight: height || 'auto' }}
    >
      {/* Search bar */}
      {isSearchable && (
        <div className="bg-white px-6 py-4 border-b border-gray-200">
          <div className="w-full max-w-md">
            <InputFieldOutlined
              placeholder="Search..."
              onChange={handleSearch}
              StartIcon={<SearchIcon className="w-5 h-5 text-[#BA141B]" />}
              inputClass="border-[#BA141B]/40 focus:border-[#BA141B] focus:ring-[#BA141B]/20"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <table className="w-full text-sm text-left text-gray-700">
        <thead
          className={`
            text-xs uppercase font-semibold tracking-wide
            bg-gray-50 border-b border-gray-200
            ${fixedHeader ? 'sticky top-0 z-10' : ''}
          `}
        >
          <tr>
            {columns.map((header, idx) => (
              <th
                key={header.accessor || idx}
                scope="col"
                className="px-6 py-4"
                style={{ width: header.width }}
              >
                <div className="flex items-center gap-2 group">
                  <span className="text-[#BA141B]">{header.name}</span>

                  {isSortable && (
                    <button
                      type="button"
                      onClick={() =>
                        handleSort(header.accessor, sortOrder[header.accessor] || 'asc')
                      }
                      className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <SortIcon
                        size={14}
                        className={`text-[#BA141B] transition-transform ${
                          sortOrder[header.accessor] === 'desc' ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
          {loader ? (
            <tr>
              <td colSpan={columns.length} className="py-12 text-center">
                <Loader className="inline-block" />
              </td>
            </tr>
          ) : filteredRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-12 text-center text-gray-500">
                No data found
              </td>
            </tr>
          ) : (
            filteredRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="bg-white hover:bg-[#F5821F]/5 transition-colors duration-150"
              >
                {columns.map((col, colIdx) => (
                  <td
                    key={col.accessor || colIdx}
                    className={`px-6 py-4 ${
                      colIdx === 0 ? 'font-medium text-gray-900' : 'text-gray-700'
                    } whitespace-nowrap`}
                  >
                    {row[col.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

export default Table